import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, MessageSquare, ShoppingCart, UserPlus, CreditCard, Check, Trash2, MailOpen, Mail } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { toast } from "sonner";

type Notif = {
  id: string;
  type: string;
  title: string;
  description: string | null;
  link: string | null;
  is_read: boolean;
  created_at: string;
};

type Msg = {
  id: string;
  name: string;
  message: string;
  created_at: string;
  read_by_admin: boolean;
};

const iconFor = (type: string) => {
  switch (type) {
    case "order":
      return ShoppingCart;
    case "user":
      return UserPlus;
    case "payment":
      return CreditCard;
    default:
      return Bell;
  }
};

const Badge = ({ count }: { count: number }) =>
  count > 0 ? (
    <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 grid place-items-center rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold ring-2 ring-background">
      {count > 99 ? "99+" : count}
    </span>
  ) : null;

export const MessageBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Msg[]>([]);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    const { data } = await supabase
      .from("messages")
      .select("id,name,message,created_at,read_by_admin")
      .order("created_at", { ascending: false })
      .limit(8);
    setItems(data ?? []);
    setUnread((data ?? []).filter((m) => !m.read_by_admin).length);
    const { count } = await supabase
      .from("messages")
      .select("id", { count: "exact", head: true })
      .eq("read_by_admin", false);
    if (typeof count === "number") setUnread(count);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-msgs-bell")
      .on("postgres_changes", { event: "*", schema: "public", table: "messages" }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const markAllRead = async () => {
    await supabase.from("messages").update({ read_by_admin: true }).eq("read_by_admin", false);
    load();
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="relative h-10 w-10" aria-label="Messages">
          <MessageSquare className="h-5 w-5" />
          <Badge count={unread} />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-[92vw] sm:w-96 p-0">
        <div className="flex items-center justify-between px-4 py-3 border-b">
          <div className="font-semibold text-sm">Messages</div>
          {unread > 0 && (
            <button onClick={markAllRead} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
              <Check className="h-3 w-3" /> Mark all read
            </button>
          )}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No messages yet</div>
          ) : (
            items.map((m) => (
              <button
                key={m.id}
                onClick={() => navigate("/admin/messages")}
                className={cn(
                  "w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-muted/60 transition-colors flex gap-3",
                  !m.read_by_admin && "bg-primary/5"
                )}
              >
                <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                  <MessageSquare className="h-4 w-4 text-primary" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-sm truncate">{m.name}</span>
                    <span className="text-[11px] text-muted-foreground shrink-0">
                      {formatDistanceToNow(new Date(m.created_at), { addSuffix: true })}
                    </span>
                  </div>
                  <p className="text-xs text-muted-foreground truncate">{m.message}</p>
                </div>
                {!m.read_by_admin && <span className="h-2 w-2 rounded-full bg-destructive mt-2 shrink-0" />}
              </button>
            ))
          )}
        </div>
        <Link to="/admin/messages" className="block text-center text-sm py-2.5 border-t hover:bg-muted/60 font-medium">
          View all messages
        </Link>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

type Filter = "all" | "order" | "payment" | "message" | "user";

const FILTERS: { key: Filter; label: string }[] = [
  { key: "all", label: "All" },
  { key: "order", label: "Orders" },
  { key: "payment", label: "Payments" },
  { key: "message", label: "Messages" },
  { key: "user", label: "Users" },
];

export const NotificationBell = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState<Notif[]>([]);
  const [unread, setUnread] = useState(0);
  const [filter, setFilter] = useState<Filter>("all");
  const [confirm, setConfirm] = useState<{ kind: "one" | "all"; id?: string } | null>(null);

  const load = async () => {
    const { data } = await supabase
      .from("notifications")
      .select("*")
      .neq("type", "message")
      .order("created_at", { ascending: false })
      .limit(50);
    setItems(data ?? []);
    const { count } = await supabase
      .from("notifications")
      .select("id", { count: "exact", head: true })
      .eq("is_read", false)
      .neq("type", "message");
    setUnread(count ?? 0);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("admin-notifs-bell")
      .on("postgres_changes", { event: "*", schema: "public", table: "notifications" }, load)
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  const filtered = useMemo(
    () => (filter === "all" ? items : items.filter((n) => n.type === filter)),
    [items, filter]
  );

  const markAllRead = async () => {
    await supabase.from("notifications").update({ is_read: true }).eq("is_read", false);
    load();
  };

  const toggleRead = async (n: Notif, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = !n.is_read;
    setItems((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: next } : x)));
    setUnread((u) => Math.max(0, u + (next ? -1 : 1)));
    const { error } = await supabase.from("notifications").update({ is_read: next }).eq("id", n.id);
    if (error) {
      toast.error("Failed to update");
      load();
    }
  };

  const deleteOne = async (id: string) => {
    const wasUnread = items.find((n) => n.id === id)?.is_read === false;
    setItems((prev) => prev.filter((n) => n.id !== id));
    if (wasUnread) setUnread((u) => Math.max(0, u - 1));
    const { error } = await supabase.from("notifications").delete().eq("id", id);
    if (error) {
      toast.error("Failed to delete");
      load();
    } else {
      toast.success("Notification deleted");
    }
  };

  const deleteAll = async () => {
    const ids = filtered.map((n) => n.id);
    if (!ids.length) return;
    setItems((prev) => prev.filter((n) => !ids.includes(n.id)));
    const { error } = await supabase.from("notifications").delete().in("id", ids);
    if (error) {
      toast.error("Failed to delete all");
      load();
    } else {
      toast.success("Notifications cleared");
      load();
    }
  };

  const open = async (n: Notif) => {
    if (!n.is_read) {
      await supabase.from("notifications").update({ is_read: true }).eq("id", n.id);
    }
    if (n.link) navigate(n.link);
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="relative h-10 w-10" aria-label="Notifications">
            <Bell className="h-5 w-5" />
            <Badge count={unread} />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-[92vw] sm:w-[26rem] p-0">
          <div className="flex items-center justify-between px-4 py-3 border-b gap-2">
            <div className="font-semibold text-sm">Notifications</div>
            <div className="flex items-center gap-3">
              {unread > 0 && (
                <button onClick={markAllRead} className="text-xs text-primary hover:underline inline-flex items-center gap-1">
                  <Check className="h-3 w-3" /> Mark all read
                </button>
              )}
              {items.length > 0 && (
                <button
                  onClick={() => setConfirm({ kind: "all" })}
                  className="text-xs text-destructive hover:underline inline-flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" /> Delete all
                </button>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1 px-2 py-2 border-b overflow-x-auto">
            {FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => setFilter(f.key)}
                className={cn(
                  "text-xs px-3 py-1.5 rounded-full whitespace-nowrap transition-colors",
                  filter === f.key
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted/60 text-muted-foreground hover:bg-muted"
                )}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="max-h-[60vh] overflow-y-auto">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">You're all caught up</div>
            ) : (
              filtered.map((n) => {
                const Icon = iconFor(n.type);
                return (
                  <div
                    key={n.id}
                    className={cn(
                      "group relative w-full px-4 py-3 border-b last:border-b-0 hover:bg-muted/60 transition-colors flex gap-3",
                      !n.is_read && "bg-primary/5"
                    )}
                  >
                    <button onClick={() => open(n)} className="flex gap-3 flex-1 min-w-0 text-left">
                      <div className="h-9 w-9 rounded-full bg-primary/10 grid place-items-center shrink-0">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium text-sm truncate">{n.title}</span>
                          <span className="text-[11px] text-muted-foreground shrink-0">
                            {formatDistanceToNow(new Date(n.created_at), { addSuffix: true })}
                          </span>
                        </div>
                        {n.description && <p className="text-xs text-muted-foreground truncate">{n.description}</p>}
                      </div>
                    </button>
                    <div className="flex items-center gap-1 shrink-0 sm:opacity-0 sm:group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => toggleRead(n, e)}
                        className="h-7 w-7 grid place-items-center rounded-md hover:bg-background"
                        aria-label={n.is_read ? "Mark as unread" : "Mark as read"}
                        title={n.is_read ? "Mark as unread" : "Mark as read"}
                      >
                        {n.is_read ? (
                          <Mail className="h-3.5 w-3.5 text-muted-foreground" />
                        ) : (
                          <MailOpen className="h-3.5 w-3.5 text-muted-foreground" />
                        )}
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          setConfirm({ kind: "one", id: n.id });
                        }}
                        className="h-7 w-7 grid place-items-center rounded-md hover:bg-destructive/10 text-destructive"
                        aria-label="Delete notification"
                        title="Delete"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    {!n.is_read && <span className="absolute left-1 top-1/2 -translate-y-1/2 h-2 w-2 rounded-full bg-destructive" />}
                  </div>
                );
              })
            )}
          </div>
        </DropdownMenuContent>
      </DropdownMenu>

      <AlertDialog open={!!confirm} onOpenChange={(o) => !o && setConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {confirm?.kind === "all" ? "Delete all notifications?" : "Delete this notification?"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {confirm?.kind === "all"
                ? "This will permanently remove all visible notifications. This action cannot be undone."
                : "Are you sure you want to delete this notification? This action cannot be undone."}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => {
                if (confirm?.kind === "all") deleteAll();
                else if (confirm?.id) deleteOne(confirm.id);
                setConfirm(null);
              }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
